import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reminderApi } from '../services/api';
import { format } from 'date-fns';

interface Reminder {
  id: string;
  type: string;
  message: string;
  triggerAt: string;
  status: 'PENDING' | 'SENT' | 'DISMISSED';
  recurrenceType?: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  recurrenceInterval?: number;
}

export default function Reminders() {
  const { babyId } = useParams<{ babyId: string }>();
  const queryClient = useQueryClient();

  const [message, setMessage] = useState('');
  const [triggerAt, setTriggerAt] = useState('');
  const [recurrenceType, setRecurrenceType] = useState('NONE');
  const [error, setError] = useState<string | null>(null);

  const { data: reminders, isLoading } = useQuery({
    queryKey: ['reminders', babyId],
    queryFn: async () => {
      if (!babyId) return [];
      const response = await reminderApi.listForBaby(babyId);
      return response.data as Reminder[];
    },
    enabled: !!babyId,
  });

  const createMutation = useMutation({
    mutationFn: (newReminder: { type: string; message: string; triggerAt: string; recurrenceType?: string; recurrenceInterval?: number }) => {
        if(!babyId) throw new Error("Baby not selected");
      return reminderApi.create(babyId, newReminder);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders', babyId] });
      setMessage('');
      setTriggerAt('');
      setRecurrenceType('NONE');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (reminderId: string) => {
      return reminderApi.delete(reminderId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders', babyId] });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!message || !triggerAt) {
      setError('Message and trigger date are required.');
      return;
    }

    createMutation.mutate({
      type: 'CUSTOM',
      message,
      triggerAt: new Date(triggerAt).toISOString(),
      recurrenceType: recurrenceType !== 'NONE' ? recurrenceType : undefined,
      recurrenceInterval: 1,
    });
  };

  if (!babyId) {
    return <div className="p-4 text-center text-gray-500">Please select a baby to see reminders.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reminders</h1>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add New Reminder</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <input
              id="message"
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="input"
              placeholder="e.g., Doctor's appointment"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="triggerAt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date and Time
              </label>
              <input
                id="triggerAt"
                type="datetime-local"
                value={triggerAt}
                onChange={(e) => setTriggerAt(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="recurrence" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Repeat
              </label>
              <select
                id="recurrence"
                value={recurrenceType}
                onChange={(e) => setRecurrenceType(e.target.value)}
                className="input"
              >
                <option value="NONE">Does not repeat</option>
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Adding...' : 'Add Reminder'}
          </button>
        </form>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upcoming Reminders</h2>
        {isLoading ? (
          <p className="text-gray-500">Loading reminders...</p>
        ) : reminders && reminders.length > 0 ? (
          <ul className="space-y-3">
            {reminders.map((reminder) => (
              <li key={reminder.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{reminder.message}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(reminder.triggerAt), 'PPp')}
                    {reminder.recurrenceType && reminder.recurrenceType !== 'NONE' && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            ↻ {reminder.recurrenceType}
                        </span>
                    )}
                    <span className={`ml-2 text-xs font-semibold ${reminder.status === 'PENDING' ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                        {reminder.status}
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => deleteMutation.mutate(reminder.id)}
                  className="text-red-500 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">No upcoming reminders.</p>
        )}
      </div>
    </div>
  );
}
